import { useEffect, useMemo, useState } from 'react'
import { pb } from '../../lib/pb'
import { money, shortDate } from '../../lib/format'
import { getSubscribers, type Subscriber } from '../../lib/adminApi'
import { planById } from '../../lib/constants'
import { Card, Badge, Input, Skeleton } from '../../components/ui'
import { EmptyState } from '../../components/EmptyState'
import '../pages.css'
import './admin.css'

type SortKey = 'name' | 'planId' | 'mrrCents' | 'seats' | 'createdAt'

const STATUS_COLOR: Record<string, string> = {
  active: 'var(--cred-live)',
  past_due: 'var(--c-warn)',
  canceled: 'var(--c-error)',
}

export function AdminSubscribers() {
  const [rows, setRows] = useState<Subscriber[] | null>(null)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'mrrCents', dir: -1 })

  useEffect(() => {
    let cancelled = false
    getSubscribers(pb.authStore.token)
      .then((d) => !cancelled && setRows(d.subscribers))
      .catch((e) => !cancelled && setError(e.message))
    return () => {
      cancelled = true
    }
  }, [])

  const view = useMemo(() => {
    if (!rows) return []
    const term = q.trim().toLowerCase()
    const filtered = term
      ? rows.filter(
          (r) =>
            r.name.toLowerCase().includes(term) ||
            r.domain.toLowerCase().includes(term) ||
            r.ownerEmail.toLowerCase().includes(term),
        )
      : rows
    const { key, dir } = sort
    return [...filtered].sort((a, b) => {
      const av = a[key]
      const bv = b[key]
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }, [rows, q, sort])

  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: key === 'name' ? 1 : -1 }))

  const totalMrr = useMemo(() => view.reduce((n, r) => n + r.mrrCents, 0), [view])

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Admin · Subscribers</h1>
          <p className="page__sub">Every customer account, their plan, seats, and recurring value.</p>
        </div>
      </div>

      <div style={{ marginBottom: '1rem', maxWidth: 320 }}>
        <Input placeholder="Search name, domain, or owner…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {error ? (
        <Card>
          <p style={{ color: 'var(--c-error)' }}>Couldn’t load subscribers: {error}</p>
        </Card>
      ) : !rows ? (
        <Card>
          <Skeleton w="100%" h={18} style={{ marginBottom: 12 }} />
          <Skeleton w="100%" h={18} style={{ marginBottom: 12 }} />
          <Skeleton w="100%" h={18} />
        </Card>
      ) : view.length === 0 ? (
        <Card>
          <EmptyState title="No accounts" body="No customer accounts match your search." />
        </Card>
      ) : (
        <Card>
          <table className="admin-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('name')}>Account</th>
                <th onClick={() => toggleSort('planId')}>Plan</th>
                <th className="is-num" onClick={() => toggleSort('mrrCents')}>
                  MRR
                </th>
                <th className="is-num" onClick={() => toggleSort('seats')}>
                  Seats
                </th>
                <th>Status</th>
                <th className="is-num" onClick={() => toggleSort('createdAt')}>
                  Joined
                </th>
              </tr>
            </thead>
            <tbody>
              {view.map((r) => (
                <tr key={r.accountId}>
                  <td>
                    <b>{r.name}</b>
                    <div className="admin-table__sub">{r.ownerEmail || r.domain || '—'}</div>
                  </td>
                  <td>
                    {planById(r.planId)?.name ?? r.planId}
                    {r.cancelAtPeriodEnd && <div className="admin-table__sub">downgrades soon</div>}
                  </td>
                  <td className="is-num tnum">{money(r.mrrCents)}</td>
                  <td className="is-num tnum">{r.seats}</td>
                  <td>
                    <Badge color={STATUS_COLOR[r.status] ?? 'var(--c-sub)'} dot>
                      {r.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="is-num">{r.createdAt ? shortDate(r.createdAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: '0.9rem', textAlign: 'right', color: 'var(--c-sub)', fontSize: '0.84rem' }}>
            {view.length} account{view.length === 1 ? '' : 's'} · {money(totalMrr)} MRR
          </div>
        </Card>
      )}
    </div>
  )
}
